import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { PDFExportComponent } from '@progress/kendo-angular-pdf-export';
import { pdf } from '@progress/kendo-drawing';
import { ICalculatorSearchViewModel } from 'src/app/shared/models/common';
@Component({
  selector: 'app-awardletter',
  templateUrl: './awardletter.component.html',
  styleUrls: ['./awardletter.component.css']
})
export class AwardletterComponent {
  private static fontsRegistered = false;
  @ViewChild('pdfExport', { static: false }) pdfExport?: PDFExportComponent;

  isPdfExporting = false;
  calSearch!: ICalculatorSearchViewModel;
  model!: ICalculatorSearchViewModel;
  today: Date = new Date();
  
  constructor(private route: Router) {
    if (!AwardletterComponent.fontsRegistered) {
      pdf.defineFont({
        'AwardSerif': 'assets/fonts/award-serif-regular.ttf',
        'AwardSerif|Bold': 'assets/fonts/award-serif-bold.ttf',
        'AwardSans': 'assets/fonts/award-sans-regular.ttf',
        'AwardSans|Bold': 'assets/fonts/award-sans-bold.ttf',
        'Georgia': 'assets/fonts/award-serif-regular.ttf',
        'Georgia|Bold': 'assets/fonts/award-serif-bold.ttf',
        'Arial': 'assets/fonts/award-sans-regular.ttf',
        'Arial|Bold': 'assets/fonts/award-sans-bold.ttf',
        'Times New Roman': 'assets/fonts/award-serif-regular.ttf',
        'Times New Roman|Bold': 'assets/fonts/award-serif-bold.ttf'
      });
      AwardletterComponent.fontsRegistered = true;
    }
  }
  @Output() cancel: EventEmitter<boolean> = new EventEmitter();
  @Input()
  set calcSearchModel(value: ICalculatorSearchViewModel) {
    this.modelinit();
    if(value  !== undefined)
    {
      this.model = value;
      this.calSearch = {
      firstName : this.model.firstName,
      lastName : this.model.lastName,
      fiscalYear : this.model.fiscalYear,
      systemID : this.model.systemID,
      schoolID : this.model.schoolID,
      qbeAmount : this.model.qbeAmount,
      birthDate : this.model.birthDate,
      gtid : this.model.gtid,
      middleName:this.model.middleName,
      displayAmount:this.model.displayAmount,
      found :this.model.found,
      orginalAmount:this.model.orginalAmount,
      percentage:this.model.percentage,
      sedAmount:this.model.sedAmount,
      currentYear:this.model.currentYear,
      previousYear: this.model.previousYear,
      contactName: this.model.contactName,
      addressLine1: this.model.addressLine1,
      addressLine2: this.model.addressLine2,
      city: this.model.city,
      state: this.model.state,
      zip: this.model.zip,
      phone: this.model.phone,
      email: this.model.email,
      message: this.model.message,
      systemName: this.model.systemName,
      schoolName: this.model.schoolName
      };
    }
  }
   get calcSearchModel(): ICalculatorSearchViewModel {
     return this.model;
  }
  modelinit():void{
    this.calSearch = {
      firstName : '',
      lastName : '',
      fiscalYear : 0,
      systemID : '',
      schoolID : '',
      qbeAmount : 0,
      birthDate : new Date(),
      gtid : '',
      middleName:'',
      displayAmount:0,
      found :1,
      orginalAmount:0,
      percentage:0,
      sedAmount:0,
      currentYear:'',
      previousYear: '',
      contactName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      email: '',
      message: '',
      systemName: '',
      schoolName: ''
      };
  }

  onClickBack(): void {
    this.cancel.emit(false);
    this.route.navigate(['/eligibilitycalculatorsearch']);
  }
  public printletter(): void{
    window.print();
  }

  public async downloadPdf(): Promise<void> {
    if (!this.pdfExport) {
      return;
    }

    const fileName = `GSNS-Eligibility-Letter-${this.calSearch.lastName || 'Student'}.pdf`;

    try {
      this.isPdfExporting = true;
      await this.waitForLayoutFrame();
      const group = await this.pdfExport.export();
      const blob = await this.exportGroupToBlob(group);

      if (this.shouldUseMobilePdfPreview()) {
        await this.openMobilePdf(blob, fileName);
        return;
      }

      this.downloadBlob(blob, fileName);
    } catch {
      this.pdfExport.saveAs(fileName);
    } finally {
      this.isPdfExporting = false;
    }
  }

  get studentName(): string {
    return [this.calSearch.firstName, this.calSearch.middleName, this.calSearch.lastName]
      .filter((value) => !!value)
      .join(' ');
  }

  get legislativeFormulaAdjustment(): number {
    return this.toAmount(this.calSearch.displayAmount) - this.toAmount(this.calSearch.qbeAmount);
  }

  get formattedSchoolYear(): string {
    const schoolYear = `${this.calSearch.currentYear || this.calSearch.fiscalYear || ''}`.trim();
    if (!schoolYear) {
      return '';
    }

    return schoolYear.includes('-')
      ? schoolYear.replace(/\s*-\s*/g, ' - ')
      : schoolYear;
  }

  get legislativeFormulaLabel(): string {
    const fiscalYear = `${this.calSearch.fiscalYear || ''}`.trim();
    const shortYear = fiscalYear.length >= 2 ? fiscalYear.slice(-2) : fiscalYear;
    return `FY${shortYear} Legislative Formula Adjustment`;
  }

  get continuationSchoolYearText(): string {
    return this.formattedSchoolYear || `${this.calSearch.fiscalYear || ''}`;
  }

  get scholarshipContactEmail(): string {
    return this.calSearch.email || 'gsns@doe.k12.ga.us';
  }

  get paymentScheduleText(): string {
    return 'The Department of Education will make quarterly scholarship payments to the parents of scholarship students on or before October 15, December 15, February 15, and May 15 for quarterly periods corresponding, respectively, to August 1 through September 30, October 1 through November 30, December 1 through the last day of February, and March 1 through May 31 during the academic year.';
  }

  get checkInstructionText(): string {
    return "As required by law, each quarterly scholarship payment is issued via paper check made payable to the student's parent and mailed by the Department to the student's participating private school. The parent must restrictively (\"for deposit only\") endorse the check to the participating school for deposit into the school's account.";
  }

  get enrollmentText(): string {
    return 'The initial payment is made upon evidence of admission to the participating school, and subsequent payments are made on evidence of continued enrollment and attendance at the participating school.';
  }

  get expenseText(): string {
    return "Your student's private school may apply the scholarship funds toward these expenses: tuition and fees; student assessment; uniforms; physical therapy, speech therapy, or occupational therapy; transportation provided by the school; meals; summer school programs; tutoring; and other services as authorized by the Department.";
  }

  get noteIntroText(): string {
    return 'The Georgia Special Needs Scholarship program is funded through state appropriations. The total scholarship amount listed above is equivalent to the costs of the educational program that would have been provided for the';
  }

  get noteContinuationText(): string {
    return 'student in the resident school system as calculated under Code Section 20-2-161.';
  }

  private toAmount(value: number | string | undefined | null): number {
    if (value === undefined || value === null) {
      return 0;
    }

    if (typeof value === 'number') {
      return value;
    }

    return Number(value.replace(/[$,]/g, '')) || 0;
  }

  private shouldUseMobilePdfPreview(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor || '';
    return /Android|webOS|iPhone|iPad|iPod|Opera Mini|IEMobile|Mobile/i.test(userAgent);
  }

  private exportGroupToBlob(group: any): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
      try {
        pdf.toBlob(group, (data: Blob) => resolve(data));
      } catch (error) {
        reject(error);
      }
    });
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  }

  private async openMobilePdf(blob: Blob, fileName: string): Promise<void> {
    const nav = navigator as Navigator & {
      canShare?: (data?: ShareData) => boolean;
      share?: (data?: ShareData) => Promise<void>;
    };

    const file = new File([blob], fileName, { type: 'application/pdf' });

    if (nav.share && nav.canShare?.({ files: [file] })) {
      await nav.share({ files: [file], title: fileName });
      return;
    }

    const blobUrl = URL.createObjectURL(blob);
    window.location.href = blobUrl;
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  }

  private waitForLayoutFrame(): Promise<void> {
    return new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
  }
}
